import {AfterViewChecked, AfterViewInit, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {Chart, ChartConfiguration, ChartDataset, registerables, TooltipItem} from "chart.js";
import {Store} from "@ngrx/store";
import {
    selectBaseScenarioCashflowsGraph,
    selectHighScenarioCashflowsGraph,
    selectLowScenarioCashflowsGraph,
    selectSelectedFundValuationCashflowsGraph,
    selectWeightedScenarioCashflowsGraph
} from "../../store/fund-valuation/fund-valuation.selectors";
import {
    selectFvSensitivityDetailsBaseScenarioCashflowsGraph,
    selectFvSensitivityDetailsHighScenarioCashflowsGraph,
    selectFvSensitivityDetailsLowScenarioCashflowsGraph,
    selectFvSensitivityDetailsWeightedScenarioCashflowsGraph
} from "../../fund-valuation-sensitivity-page/store/fv-sensitivity.selectors";
import {map, Observable, Subscription} from "rxjs";
import {DateTime} from "luxon";
import "chartjs-adapter-luxon";
import {FundCashflowGraphData} from "../../utils/fund-cash-flows-util";
import {CardChartSource} from "../../../shared/comments/card-context-menu/card-menu.types";
import {chartImageSource} from "../../../shared/comments/card-context-menu/chart-image-source.util";

@Component({
    selector: "valumize-fund-cashflows-graph",
    templateUrl: "./fund-cashflows-graph.component.html",
    styleUrls: ["./fund-cashflows-graph.component.scss"],
    standalone: false
})
export class FundCashflowsGraphComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
    private readonly store = inject(Store);

    @Input() isSensitivityView = false;

    fundValuationCashflowsGraph$ = this.store.select(selectSelectedFundValuationCashflowsGraph);
    lowScenarioCashflowsGraph$!: Observable<FundCashflowGraphData>;
    baseScenarioCashflowsGraph$!: Observable<FundCashflowGraphData>;
    highScenarioCashflowsGraph$!: Observable<FundCashflowGraphData>;
    weightedScenarioCashflowsGraph$!: Observable<FundCashflowGraphData>;

    subscriptions: Subscription[] = [];
    private scenarioSubscriptions: Subscription[] = [];
    graphPanelOpenState = true;
    scenarioGraphPanelOpenState = true;

    private chart: Chart | undefined;
    private lowChart: Chart | undefined;
    private baseChart: Chart | undefined;
    private highChart: Chart | undefined;
    private weightedChart: Chart | undefined;

    /** Card-menu image source: the combined cashflows chart (falls back to the weighted scenario). */
    readonly cardChartSource: CardChartSource = chartImageSource(() => this.chart ?? this.weightedChart);

    constructor() {
    }

    ngOnInit(): void {
        this.bindScenarioObservables();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes["isSensitivityView"];
        if (!change || change.firstChange) return;
        // Re-bind to the correct store branch and discard existing scenario charts +
        // their subscriptions so ngAfterViewChecked rebuilds them against the new source.
        this.bindScenarioObservables();
        this.scenarioSubscriptions.forEach(s => s.unsubscribe());
        this.scenarioSubscriptions = [];
        this.clearScenarioCharts();
    }

    private bindScenarioObservables(): void {
        this.lowScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectFvSensitivityDetailsLowScenarioCashflowsGraph)
            : this.store.select(selectLowScenarioCashflowsGraph);
        this.baseScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectFvSensitivityDetailsBaseScenarioCashflowsGraph)
            : this.store.select(selectBaseScenarioCashflowsGraph);
        this.highScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectFvSensitivityDetailsHighScenarioCashflowsGraph)
            : this.store.select(selectHighScenarioCashflowsGraph);
        this.weightedScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectFvSensitivityDetailsWeightedScenarioCashflowsGraph)
            : this.store.select(selectWeightedScenarioCashflowsGraph);
    }

    private clearScenarioCharts(): void {
        if (this.lowChart) { this.lowChart.destroy(); this.lowChart = undefined; }
        if (this.baseChart) { this.baseChart.destroy(); this.baseChart = undefined; }
        if (this.highChart) { this.highChart.destroy(); this.highChart = undefined; }
        if (this.weightedChart) { this.weightedChart.destroy(); this.weightedChart = undefined; }
    }

    ngAfterViewInit() {
        Chart.register(...registerables);

        // Main aggregate chart is hidden in sensitivity view; skip its setup so we
        // don't attempt new Chart(null, ...) when the canvas isn't rendered.
        if (this.isSensitivityView) {
            return;
        }

        const ctx = document.getElementById("FundCashFlowsGraph") as HTMLCanvasElement;

        this.subscriptions.push(
            this.fundValuationCashflowsGraph$.pipe(map(graph => {
                if (this.chart) {
                    this.chart.destroy();
                }
                if (graph.labels.length > 0) {
                    this.chart = new Chart(ctx, {
                        type: "line",
                        data: graph,
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: false,
                            scales: {
                                x: {
                                    type: "time",
                                    time: {
                                        unit: "year", // Set the interval granularity; can also be set to quarter
                                        displayFormats: {
                                            quarter: "MMM yyyy"
                                        },
                                        tooltipFormat: "MMM yyyy"
                                    },
                                    ticks: {
                                        source: "auto",
                                    },
                                    bounds: "ticks"
                                },
                                y: {
                                    beginAtZero: true,
                                    grid: {
                                        color: (context) => {
                                            if (context.tick.value === 0) {
                                                return "rgba(0,0,0,0.2)";
                                            }
                                            return "rgba(0,0,0,0.1)";
                                        },
                                        lineWidth: (context) => context.tick.value === 0 ? 3 : 1
                                    }
                                }
                            }
                        }
                    });
                }
            })).subscribe()
        );
    }

    ngAfterViewChecked() {
        const lowCtx = document.getElementById("LowScenarioCashFlowsGraph") as HTMLCanvasElement;
        const baseCtx = document.getElementById("BaseScenarioCashFlowsGraph") as HTMLCanvasElement;
        const highCtx = document.getElementById("HighScenarioCashFlowsGraph") as HTMLCanvasElement;
        const weightedCtx = document.getElementById("WeightedScenarioCashFlowsGraph") as HTMLCanvasElement;

        if (lowCtx && !this.lowChart) {
            this.setupBarChart(this.lowScenarioCashflowsGraph$, lowCtx, chart => this.lowChart = chart);
        }
        if (baseCtx && !this.baseChart) {
            this.setupBarChart(this.baseScenarioCashflowsGraph$, baseCtx, chart => this.baseChart = chart);
        }
        if (highCtx && !this.highChart) {
            this.setupBarChart(this.highScenarioCashflowsGraph$, highCtx, chart => this.highChart = chart);
        }
        if (weightedCtx && !this.weightedChart) {
            this.setupBarChart(this.weightedScenarioCashflowsGraph$, weightedCtx, chart => this.weightedChart = chart);
        }
    }

    private setupBarChart(dataObservable$: Observable<FundCashflowGraphData>, canvas: HTMLCanvasElement, setChartInstance: (chart: Chart) => void) {
        this.scenarioSubscriptions.push(
            dataObservable$.pipe(map(graph => {
                if (canvas?.getContext("2d")) {
                    this.destroyExistingChart(canvas);

                    if (graph && graph.datasets.some(ds => ds.data && ds.data.length > 0)) {
                        const newChart = new Chart(canvas, this.getChartConfig(graph));
                        setChartInstance(newChart);
                    }
                }
            })).subscribe()
        );
    }

    private destroyExistingChart(canvas: HTMLCanvasElement): void {
        if (canvas.id === "LowScenarioCashFlowsGraph" && this.lowChart) {
            this.lowChart.destroy();
        }
        if (canvas.id === "BaseScenarioCashFlowsGraph" && this.baseChart) {
            this.baseChart.destroy();
        }
        if (canvas.id === "HighScenarioCashFlowsGraph" && this.highChart) {
            this.highChart.destroy();
        }
        if (canvas.id === "WeightedScenarioCashFlowsGraph" && this.weightedChart) {
            this.weightedChart.destroy();
        }
    }

    private getChartConfig(graphData: FundCashflowGraphData): ChartConfiguration {
        return {
            type: "bar",
            data: {
                datasets: graphData.datasets as ChartDataset<"bar" | "line", { x: number; y: number }[]>[]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        stacked: true,
                        grid: {
                            color: (context) => context.tick.value === 0 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)",
                            lineWidth: (context) => context.tick.value === 0 ? 3 : 1
                        }
                    },
                    x: {
                        type: "time",
                        time: {
                            unit: "year", // Set the interval granularity; can also be set to quarter
                            displayFormats: {
                                quarter: "MMM yyyy"
                            },
                            tooltipFormat: "MMM yyyy"
                        },
                        ticks: {
                            source: "auto",
                        },
                        bounds: "ticks",
                        grid: {
                            offset: false
                        },
                        offset: false,
                    }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: this.generateTooltipLabel(graphData)
                        }
                    },
                    legend: {
                        display: true
                    }
                }
            }
        };
    }

    private generateTooltipLabel(graphData: FundCashflowGraphData) {
        return (context: TooltipItem<"bar" | "line">) => {
            const dataPoint = context.raw as { x: number; y: number };

            const label = context.dataset.label ?? "";
            const value = dataPoint.y;
            const lines = [`${label}: ${value}`];

            if (context.dataset.label === "Asset Distribution") {
                const quarterDate = DateTime.fromMillis(dataPoint.x);

                const quarterLabel = quarterDate.toFormat("yyyy-MM-dd");
                const assetDetails = graphData.assetDistribution[quarterLabel];
                if (assetDetails && Object.keys(assetDetails).length > 0) {
                    Object.entries(assetDetails).forEach(([company, amount]) => {
                        lines.push(`${company}: ${amount}`);
                    });
                } else {
                    lines.push("No details available");
                }
            }
            return lines;
        };
    }

    private destroyCharts() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = undefined;
        }
        if (this.lowChart) {
            this.lowChart.destroy();
            this.lowChart = undefined;
        }
        if (this.baseChart) {
            this.baseChart.destroy();
            this.baseChart = undefined;
        }
        if (this.highChart) {
            this.highChart.destroy();
            this.highChart = undefined;
        }
        if (this.weightedChart) {
            this.weightedChart.destroy();
            this.weightedChart = undefined;
        }
    }

    hasGraphData(graphData: FundCashflowGraphData): boolean {
        return graphData.datasets.some(ds => ds.data && ds.data.length > 0);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.scenarioSubscriptions.forEach(sub => sub.unsubscribe());
        this.destroyCharts();
    }
}
